import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChartBar, CaretDown, CaretUp, TrendUp, TrendDown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1).toLocaleString('en-US', { month: 'short' });
};

const MaterialsSpendReport = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/shopping-lists/reports/spend`, authHeader())
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  if (!data || !data.totals || data.totals.runs === 0) return null;

  const { totals, by_job, by_month } = data;
  const overBudget = totals.variance > 0;
  const maxMonth = Math.max(...by_month.map((m) => m.spent), 1);

  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-2xl shadow-sm overflow-hidden" data-testid="materials-spend-report">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 text-left" data-testid="spend-report-toggle">
        <div className="w-10 h-10 rounded-xl bg-[#EA580C]/10 flex items-center justify-center shrink-0">
          <ChartBar size={20} weight="fill" className="text-[#EA580C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-neutral-900 dark:text-white">Materials Spend</p>
          <p className="text-xs text-neutral-500">
            {money(totals.total_spent)} across {totals.runs} run{totals.runs !== 1 ? 's' : ''} · {totals.items_purchased} items
          </p>
        </div>
        <span className={`hidden sm:inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${overBudget ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`} data-testid="spend-variance-badge">
          {overBudget ? <TrendUp size={13} weight="bold" /> : <TrendDown size={13} weight="bold" />}
          {overBudget ? '+' : ''}{money(totals.variance)} vs est.
        </span>
        {open ? <CaretUp size={16} className="text-neutral-400 shrink-0" /> : <CaretDown size={16} className="text-neutral-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-3 text-center">
              <p className="text-base sm:text-lg font-bold text-neutral-900 dark:text-white">{money(totals.total_spent)}</p>
              <p className="text-[10px] uppercase font-bold text-neutral-400">Spent</p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-3 text-center">
              <p className="text-base sm:text-lg font-bold text-neutral-900 dark:text-white">{money(totals.total_estimated)}</p>
              <p className="text-[10px] uppercase font-bold text-neutral-400">Estimated</p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-3 text-center">
              <p className={`text-base sm:text-lg font-bold ${overBudget ? 'text-red-500' : 'text-green-600'}`}>
                {overBudget ? '+' : ''}{money(totals.variance)}
              </p>
              <p className="text-[10px] uppercase font-bold text-neutral-400">Variance</p>
            </div>
          </div>

          {by_month.length > 0 && (
            <div data-testid="spend-month-bars">
              <p className="text-xs font-bold uppercase text-neutral-500 mb-2">By Month</p>
              <div className="flex items-end gap-2 h-20">
                {by_month.map((m) => (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[9px] font-bold text-neutral-500">{money(m.spent).replace('.00', '')}</span>
                    <div className="w-full bg-[#EA580C]/80 rounded-t-md" style={{ height: `${Math.max((m.spent / maxMonth) * 56, 4)}px` }} />
                    <span className="text-[10px] font-bold text-neutral-400">{monthLabel(m.month)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div data-testid="spend-by-job">
            <p className="text-xs font-bold uppercase text-neutral-500 mb-2">By Job</p>
            <div className="space-y-1.5">
              {by_job.map((j) => (
                <div key={j.job_id || 'none'} className="flex items-center justify-between text-sm bg-neutral-50 dark:bg-neutral-800 rounded-xl px-3 py-2">
                  <span className="text-neutral-700 dark:text-neutral-300 font-medium truncate">
                    {j.job_title} <span className="text-neutral-400 text-xs">· {j.runs} run{j.runs !== 1 ? 's' : ''}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="font-bold text-neutral-900 dark:text-white">{money(j.spent)}</span>
                    {j.estimated > 0 && (
                      <span className={`text-[10px] font-bold ${j.variance > 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {j.variance > 0 ? '+' : ''}{money(j.variance)}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsSpendReport;
